import React, { useState } from 'react';
import { localized } from '../i18n/translations';
import CustomImage from "../components/CustomImage";
import backgroundImage from '../images/background3.jpg';
import newYearBackground from '../images/background.webp';
import { BrowserRouter, Routes, Route, Link, NavLink } from "react-router-dom"
import axios from 'axios';
import apiUrl from '../components/Url';
import InstagramGallery from "../components/Instagram";
import NotifyAvailable from "../components/NotifyAvailable";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Slide } from 'react-slideshow-image';
import Cookies from "../components/Cookies";
import InstagramEmbed from "../components/InstagramEmbed";
import PriceDisplay from '../components/PriceDisplay';
import Snow from "../components/Snow";

// Holiday season: New Year image + snow on the hero, linking to the New Year collection.
// Set to false after the holidays to bring back the regular hero.
const NEW_YEAR_SEASON = true;

export default function HomeScreen({ addToCart, productsData, cartItems, currency, translate, country, apiCountry, categories, persolaneIlustracijeSlike }) {


    const [showModal, setShowModal] = React.useState(false);
    const [notifyProductId, setNotifyProductId] = React.useState(null);
    const [notifyCategoryId, setNotifyCategoryId] = React.useState(null);
    
    // Carousel state
    const [currentSlide, setCurrentSlide] = React.useState(0);
    
    // Featured products state
    const [featuredProducts, setFeaturedProducts] = React.useState([]);
    const [featuredLoading, setFeaturedLoading] = React.useState(true);
    
    // Partnership products state
    const [partnershipProducts, setPartnershipProducts] = React.useState([]);
    const [partnershipLoading, setPartnershipLoading] = React.useState(true);
    
    // Countdown state for Slatka kolekcija
    const [countdown, setCountdown] = React.useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        finished: false
    });
    
    // Find the main collection (is_main_collection flag)
    const slatkaCollection = categories.find(cat =>
        cat.is_main_collection === true || cat.is_main_collection === 1
    );

    // Synchronous check — no useEffect delay, available on same render as slatkaCollection
    const isCollectionLive = React.useMemo(() => {
        if (!slatkaCollection?.countdown_date) return true;
        const dateStr = slatkaCollection.countdown_date;
        const utcDateStr = dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('-', 10)
            ? dateStr : dateStr + 'Z';
        return new Date(utcDateStr).getTime() < Date.now();
    }, [slatkaCollection]);
    
    // Get countdown date from collection, or use null if not set
    // Treat the date as UTC to match backend timezone
    const countdownDate = slatkaCollection?.countdown_date ? (() => {
        // Parse the date string and treat it as UTC
        const dateStr = slatkaCollection.countdown_date;
        // If it doesn't have timezone info, append 'Z' to indicate UTC
        const utcDateStr = dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('-', 10) 
            ? dateStr 
            : dateStr + 'Z';
        return new Date(utcDateStr).getTime();
    })() : null;
    
    // Update countdown timer
    React.useEffect(() => {
        if (!countdownDate) {
            // No countdown date set, consider it finished
            setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: true });
            return;
        }
        
        let wasFinished = false;
        
        const updateCountdown = () => {
            // Use UTC time to match backend
            const now = new Date().getTime();
            const distance = countdownDate - now;
            
            if (distance < 0) {
                setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: true });
                
                // Dispatch event when countdown first finishes to trigger data refetch
                if (!wasFinished) {
                    wasFinished = true;
                    window.dispatchEvent(new CustomEvent('countdownFinished'));
                }
                return;
            }
            
            wasFinished = false;
            setCountdown({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000),
                finished: false
            });
        };
        
        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        
        return () => clearInterval(interval);
    }, [countdownDate]);
    
    // Preserve scroll position when countdown finishes
    React.useEffect(() => {
        if (countdown.finished) {
            // Store scroll position when countdown finishes
            const scrollPosition = window.scrollY;
            
            // Restore scroll position after DOM updates
            const restoreScroll = () => {
                requestAnimationFrame(() => {
                    window.scrollTo(0, scrollPosition);
                });
            };
            
            // Use setTimeout to ensure DOM has updated
            setTimeout(restoreScroll, 0);
        }
    }, [countdown.finished]);

    // Fetch Slatka kolekcija products from the collection category
    React.useEffect(() => {
        const fetchFeaturedProducts = async () => {
            try {
                setFeaturedLoading(true);
                
                // If we have the slatka collection, filter products from that category
                if (slatkaCollection && slatkaCollection.id) {
                    const collectionProducts = productsData.filter(product => product.category === slatkaCollection.id);
                    setFeaturedProducts(collectionProducts || []);
                } else {
                    // Fallback: if no collection found, use empty array
                    setFeaturedProducts([]);
                }
            } catch (error) {
                console.error('Error fetching collection products:', error);
                setFeaturedProducts([]);
            } finally {
                setFeaturedLoading(false);
            }
        };

        if (productsData.length > 0 && slatkaCollection) {
            fetchFeaturedProducts();
        } else if (productsData.length > 0 && !slatkaCollection) {
            // If no collection found, set empty array
            setFeaturedProducts([]);
            setFeaturedLoading(false);
        }
    }, [productsData, slatkaCollection]); // Fetch when productsData or slatkaCollection changes
    
    // Fetch partnership products from API
    React.useEffect(() => {
        const fetchPartnershipProducts = async () => {
            try {
                setPartnershipLoading(true);
                // Filter partnership products from existing products data
                const partnership = productsData.filter(product => product.partnership_atelje === true);
                setPartnershipProducts(partnership || []);
            } catch (error) {
                console.error('Error fetching partnership products:', error);
                setPartnershipProducts([]);
            } finally {
                setPartnershipLoading(false);
            }
        };

        if (productsData.length > 0) {
            fetchPartnershipProducts();
        }
    }, [productsData]);
    
    // Responsive items per view
    const [itemsPerView, setItemsPerView] = React.useState(4);
    
    React.useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth >= 1024) {
                setItemsPerView(4); // Always show 4 on desktop to create carousel effect
            } else if (window.innerWidth >= 640) {
                setItemsPerView(2); // Show 2 on tablet
            } else {
                setItemsPerView(1); // Show 1 on mobile
            }
        };
        
        updateItemsPerView();
        window.addEventListener('resize', updateItemsPerView);
        return () => window.removeEventListener('resize', updateItemsPerView);
    }, []);
    
    // Carousel navigation
    const maxSlide = Math.max(0, featuredProducts.length - itemsPerView);
    
    const goToNextSlide = () => {
        setCurrentSlide(prev => Math.min(prev + 1, maxSlide));
    };
    
    const goToPrevSlide = () => {
        setCurrentSlide(prev => Math.max(prev - 1, 0));
    };
    
    // Reset slide if out of bounds due to resize
    React.useEffect(() => {
        if (currentSlide > maxSlide) {
            setCurrentSlide(maxSlide);
        }
    }, [currentSlide, maxSlide]);
    
    // Auto-play carousel with pause on hover
    const [isHovered, setIsHovered] = React.useState(false);
    
    React.useEffect(() => {
        if (featuredProducts.length <= itemsPerView || isHovered) return;
        
        const interval = setInterval(() => {
            setCurrentSlide(prev => {
                if (prev >= maxSlide) {
                    return 0; // Reset to beginning
                }
                return prev + 1;
            });
        }, 4000); // Change slide every 4 seconds
        
        return () => clearInterval(interval);
    }, [featuredProducts.length, itemsPerView, maxSlide, isHovered]);

    const scrollToCategories = (e) => {
        e.preventDefault();
        const categoriesSection = document.getElementById('kategorije');
        if (categoriesSection) {
            categoriesSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const closeModal = () => {
        setShowModal(false);
    };


    const [instaWidth, setInstaWidth] = React.useState('250px');
    const [instaHeight, setInstaHeight] = React.useState('440px');

    // Function to update width based on screen size
    const updateWidth = React.useCallback(() => {

        if (window.innerWidth < 400) {
            setInstaWidth('250px'); // Small screens
            setInstaHeight('440px');
        }else if (window.innerWidth < 640) {
          setInstaWidth('300px'); // Small screens
          setInstaHeight('450px');
        } else if (window.innerWidth < 768) {
          setInstaWidth('400px'); // Medium screens
          setInstaHeight('700px');
        } else if (window.innerWidth < 1024) {
          setInstaWidth('600px'); // Large screens
          setInstaHeight('800px');
        } else {
          setInstaWidth('700px'); // Extra large screens
          setInstaHeight('900px');
        }
      }, []);
    
      React.useEffect(() => {
        // Set initial width
        updateWidth();
    
        // Add event listener for resize
        window.addEventListener('resize', updateWidth);
    
        // Cleanup event listener on unmount
        return () => {
          window.removeEventListener('resize', updateWidth);
        };
      }, [updateWidth]);




    const productsItems = productsData.map(product => (

        <div className="p-4 text-white" key={product.id}>
            <Link to={`/proizvod/${product.category}/${product.path}`}>
                <div className="relative">
                    <CustomImage src={product.imageUrl} alt={product.imageAlt} className="border-2 border-brownSkin shadow rounded-xl" />
                    {/* Sale Badge */}
                    {product.is_on_sale && product.sale_discount_percentage > 0 && (
                        <div className="absolute top-2 right-2">
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                                -{product.sale_discount_percentage}%
                            </span>
                        </div>
                    )}
                </div>
                <div>
                    <div className="flex items-center justify-center text-gray-500">
                        <span>{product.category} </span>
                    </div>
                    <div className="flex items-center justify-center">
                        <p className="text-black font-medium text-xl text-center">{localized(product, 'name', country)}</p>
                    </div>
                    <div className="flex items-center justify-center">
                        <PriceDisplay product={product} currency={currency} className="text-center" />
                    </div>
                </div>
            </Link>


            <div className="flex items-center justify-center mt-2">
                {product.available ? <button onClick={() => addToCart(product.id, 1)} className="text-black bg-darkerSkin font-sans px-8 py-2">{cartItems.some(item => item.id === product.id) ? translate('Added to cart') : translate("Add to cart")}</button> : <button onClick={() => { setShowModal(true); setNotifyProductId(product.id); setNotifyCategoryId(null); }} className="text-black font-sans bg-red-200 px-8 py-2">{translate("Notify me when available")}</button>}
            </div>

        </div>

    ));


    return (

        <div className="bg-white">
            <SEO
                title="Bulka Ceramics - Ručno rađeni keramički proizvodi"
                description="Bulka Ceramics nudi jedinstvene keramičke proizvode. Istražite našu kolekciju ručno rađene keramike i umetničkih dodataka."
                type="website" 
                name="Bulka Ceramics" 
            />
            <Cookies />

            {showModal && (
                <NotifyAvailable
                    onClose={closeModal}
                    notifyProductId={notifyProductId}
                    notifyCategoryId={notifyCategoryId}
                    country={country}
                />
            )}

            {/* Enhanced Hero Section */}
            <div
                className="relative flex flex-col h-[90vh] md:h-[95vh] bg-cover items-center justify-center bg-center bg-no-repeat"
                style={NEW_YEAR_SEASON
                    ? { backgroundImage: `url(${newYearBackground})` }
                    : { backgroundImage: `url(${backgroundImage})`, backgroundPosition: 'center 40%' }
                }
            >
                {NEW_YEAR_SEASON && (
                    <>
                        <div className="absolute inset-0 bg-black/20"></div>
                        {/* Snow only on hero background */}
                        <Snow />
                    </>
                )}
                <div className="relative items-center justify-center flex-col px-2 text-center flex z-10 gap-4 sm:gap-6 md:gap-8">
                    <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-[#facb8c] tracking-widest font-extrabold drop-shadow-lg">
                        Bulka Ceramics
                    </h1>
                    {NEW_YEAR_SEASON ? (
                        <>
                            <h2 className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-light tracking-wide drop-shadow">
                                🎄 {translate("New Year collection is coming soon")}
                            </h2>
                            <Link
                                to="/kolekcije/novogodisnja"
                                className="bg-[#facb8c] text-black px-6 sm:px-8 py-2 sm:py-3 rounded-full text-base sm:text-lg font-medium hover:bg-[#f5b76b] transition-all duration-300"
                            >
                                {translate("Notify me")}
                            </Link>
                        </>
                    ) : slatkaCollection && (
                        <>
                            <h2 className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-light tracking-wide">
                                {isCollectionLive
                                    ? `${slatkaCollection.collection_name || slatkaCollection.title} ${translate("has arrived!")}`
                                    : `${slatkaCollection.collection_name || slatkaCollection.title} ${translate("is coming soon!")}`
                                }
                            </h2>
                            {slatkaCollection.collection_url && (
                                <Link
                                    to={`/kolekcije/${slatkaCollection.collection_url}`}
                                    className="bg-[#facb8c] text-black px-6 sm:px-8 py-2 sm:py-3 rounded-full text-base sm:text-lg font-medium hover:bg-[#f5b76b] transition-all duration-300"
                                >
                                    {translate("View the summer collection")}
                                </Link>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Slatka kolekcija Section */}
            <section className="bg-bgSkin pt-16 pb-12">
                <div className="w-full mx-auto px-4 sm:px-6 lg:px-12">
                    {/* Section Header */}
                    <div className="text-center mb-12">
                        <div className="mb-8">
                            <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                                {slatkaCollection?.collection_name || slatkaCollection?.titleInCategory || slatkaCollection?.title || 'Slatka kolekcija'}
                            </h2>
                            <div className="w-24 h-1 bg-darkerSkin mx-auto rounded-full"></div>
                        </div>
                    </div>

                    {/* Countdown or Products - Use min-height to prevent layout shift */}
                    <div className="min-h-[600px]">
                    {!countdown.finished ? (
                        /* Countdown Display */
                        <div className="flex flex-col items-center justify-center py-12">
                            {/* Mystery Products Preview */}
                            <div className="w-full mb-12">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {Array.from({ length: 4 }).map((_, index) => (
                                        <div key={index} className="group bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl border-2 border-dashed border-gray-300">
                                            {/* Mystery Image Container */}
                                            <div className="relative overflow-hidden aspect-square bg-gradient-to-br from-gray-100 to-gray-200">
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    {/* Question Mark Icon */}
                                                    <div className="w-24 h-24 bg-gray-400 rounded-full flex items-center justify-center shadow-lg">
                                                        <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                                {/* Coming Soon Overlay */}
                                                <div className="absolute inset-0 bg-brownSkin/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            </div>

                                            {/* Mystery Product Info */}
                                            <div className="p-4">
                                                <div className="text-center">
                                                    <h3 className="font-semibold text-gray-600 mb-2">
                                                        {translate("Coming soon")}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 mb-3">
                                                        {slatkaCollection?.collection_name || slatkaCollection?.title || 'Slatka kolekcija'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="text-center mb-8">
                                <p className="text-xl md:text-2xl text-gray-700 mb-6">
                                    {translate("New collection coming soon!")}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 w-full max-w-4xl">
                                {/* Days */}
                                <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                                        {countdown.days}
                                    </div>
                                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                                        {translate("Days")}
                                    </div>
                                </div>
                                {/* Hours */}
                                <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                                        {countdown.hours}
                                    </div>
                                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                                        {translate("Hours")}
                                    </div>
                                </div>
                                {/* Minutes */}
                                <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                                        {countdown.minutes}
                                    </div>
                                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                                        {translate("Minutes")}
                                    </div>
                                </div>
                                {/* Seconds */}
                                <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                                        {countdown.seconds}
                                    </div>
                                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                                        {translate("Seconds")}
                                    </div>
                                </div>
                            </div>
                            <div className="text-center mt-8">
                                {slatkaCollection?.countdown_date && (
                                    <button
                                        onClick={() => { setShowModal(true); setNotifyProductId(null); setNotifyCategoryId(slatkaCollection?.id || null); }}
                                        className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-medium bg-brownSkin text-white shadow-lg hover:shadow-xl hover:bg-brownSkin/90 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brownSkin/40 focus:ring-offset-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0a3 3 0 11-6 0h6z"/>
                                        </svg>
                                        {translate("Notify me when available")}
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Products Carousel */
                        <>
                    <div 
                        className="relative"
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {featuredLoading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg animate-pulse">
                                        <div className="aspect-square bg-gray-200"></div>
                                        <div className="p-4">
                                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-6 bg-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                <div className="carousel-container overflow-hidden">
                                    <div className="carousel-track flex transition-transform duration-500 ease-in-out" 
                                         style={{ transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)` }}>
                                        {featuredProducts.map((product, index) => (
                                            <div 
                                                key={product.id} 
                                                className="carousel-item flex-none w-full sm:w-1/2 lg:w-1/4 px-3"
                                                style={{ minWidth: `${100 / itemsPerView}%` }}
                                            >
                                                <div className="group bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl">
                                                    {/* Product Image Container */}
                                                    <div className="relative overflow-hidden aspect-square">
                                                        <Link to={`/proizvod/${product.category}/${product.path}`}>
                                                            <CustomImage 
                                                                src={product.imageUrl} 
                                                                alt={product.imageAlt || localized(product, 'name', country)} 
                                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                                                            />
                                                            {/* Sale Badge */}
                                                            {product.is_on_sale && product.sale_discount_percentage > 0 && (
                                                                <div className="absolute top-3 right-3 z-10">
                                                                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                                                                        -{product.sale_discount_percentage}%
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {/* Rasprodato (Sold Out) Badge */}
                                                            {((product.stock_tracking_enabled && product.stock_quantity === 0) || !product.is_in_stock) && (
                                                                <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-12' : 'top-3'} right-3 z-10`}>
                                                                    <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                                                                        {translate("Sold out")}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {/* Hover Overlay */}
                                                            <div className="absolute inset-0 bg-brownSkin/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                                        </Link>
                                                        
                                                        {/* Quick Buy Button */}
                                                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 translate-y-full group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                            {product.available && product.is_in_stock ? (
                                                                <button 
                                                                    onClick={() => addToCart(product.id, 1)}
                                                                    className="bg-brownSkin text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-brownSkin/90 transition-colors duration-300 shadow-lg flex items-center gap-2 whitespace-nowrap"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01"/>
                                                                    </svg>
                                                                    {cartItems.some(item => item.id === product.id) ? translate('Added to cart') : translate("Add to cart")}
                                                                </button>
                                                            ) : (
                                                                <button 
                                                                    onClick={() => { setShowModal(true); setNotifyProductId(product.id); }}
                                                                    className="bg-red-400 text-white px-4 py-2 rounded-full text-xs font-medium hover:bg-red-500 transition-colors duration-300 shadow-lg whitespace-nowrap"
                                                                >
                                                                    {translate("Notify me when available")}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Product Info */}
                                                    <div className="p-4">
                                                        <Link to={`/proizvod/${product.category}/${product.path}`}>
                                                            <div className="text-center">

                                                                <h3 className="font-semibold text-brownSkin mb-2 hover:text-brownSkin/80 transition-colors duration-300 line-clamp-2">
                                                                    {localized(product, 'name', country)}
                                                                </h3>
                                                                <div className="flex justify-center">
                                                                    <PriceDisplay product={product} currency={currency} className="text-lg font-bold text-brownSkin" />
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Navigation Arrows */}
                                {featuredProducts.length > itemsPerView && (
                                    <>
                                        <button 
                                            onClick={goToPrevSlide}
                                            disabled={currentSlide === 0}
                                            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white shadow-lg rounded-full p-3 hover:bg-darkerSkin transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                                        >
                                            <svg className="w-6 h-6 text-brownSkin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                                            </svg>
                                        </button>
                                        <button 
                                            onClick={goToNextSlide}
                                            disabled={currentSlide >= maxSlide}
                                            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white shadow-lg rounded-full p-3 hover:bg-darkerSkin transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                                        >
                                            <svg className="w-6 h-6 text-brownSkin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                                {/* Carousel Dots */}
                                {featuredProducts.length > itemsPerView && (
                                    <div className="flex justify-center mt-8 space-x-2">
                                        {Array.from({ length: maxSlide + 1 }).map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setCurrentSlide(index)}
                                                className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                                                    currentSlide === index ? 'bg-brownSkin' : 'bg-brownSkin/30'
                                                }`}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* View Collection Button */}
                                {slatkaCollection?.collection_url && (
                                    <div className="text-center mt-12">
                                        <Link 
                                            to={`/kolekcije/${slatkaCollection.collection_url}`}
                                            className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full text-lg font-medium hover:bg-brownSkin hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
                                        >
                                            {translate("View the entire collection")}
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                                            </svg>
                                        </Link>
                                    </div>
                                )}
                        </>
                    )}
                    </div>
                </div>
            </section>

            {/* Enhanced Categories Section */}
            <div className="w-full flex-col items-center justify-center flex mt-28 a" id="kategorije">
              

                <div className="flex items-center justify-center w-full">
                    <div className="w-[80%]">
                        <div className={
                            categories.filter(c => (c.show_on_homepage === true || c.show_on_homepage === 1)).length < 4
                                ? "flex flex-wrap justify-center gap-6 mt-6"
                                : "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 mt-6 space-x-4"
                        }>
                            {categories
                                // Homepage should show both normal categories and collections (when enabled)
                                .filter(category => (category.show_on_homepage === true || category.show_on_homepage === 1))
                                .map(category => {
                                    const isCollection = (category.is_collection === true || category.is_collection === 1);
                                    const linkTo = isCollection
                                        ? `/kolekcije/${category.collection_url || category.path}`
                                        : `/kategorija/${category.path}`;

                                    const title = category.collection_name || category.titleInCategory || category.title;
                                    const description = category.descriptionInCategory || category.description;

                                    return (
                                        <Link to={linkTo} key={category.id} className="items-center justify-center flex flex-col">
                                            <div><CustomImage src={"storage/" + category.image} alt={category.imageAlt} className="shadow rounded-xl max-h-[420px]" /></div>
                                            <div className="mt-2 text-center">
                                                <div className="flex items-center justify-center">
                                                    <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                                                </div>
                                                <div className="flex items-center justify-center">
                                                    <p className="text-gray-600 mt-1 text-sm">{description}</p>
                                                </div>
                                                <div>
                                                    <div className="w-full items-center justify-center flex mt-2">
                                                        <button className="bg-darkerSkin text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-[#d4a76a] transition-colors duration-300 mb-6">
                                                            {category.buttonText}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Partnership Products Section */}
            {partnershipProducts.length > 0 && (
                <div className="bg-white py-20">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                                Bulkaceramics × AteljeStojanović
                            </h2>
                            <div className="w-24 h-1 bg-[#facb8c] rounded-full mx-auto mb-6"></div>
                            <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
                            {translate("Exclusive collection of ceramic products created through the Bulkaceramics and AteljeStojanović collaboration.")} {translate("Each piece is handmade with special attention to detail.")}
                            </p>
                        </div>
                        
                        {partnershipLoading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg animate-pulse">
                                        <div className="aspect-square bg-gray-200"></div>
                                        <div className="p-4">
                                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-6 bg-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {partnershipProducts.map((product) => (
                                    <div key={product.id} className="group bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl">
                                        {/* Product Image Container */}
                                        <div className="relative overflow-hidden aspect-square">
                                            <Link to={`/proizvod/${product.category}/${product.path}`}>
                                                <CustomImage 
                                                    src={product.imageUrl} 
                                                    alt={product.imageAlt || localized(product, 'name', country)} 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                                                />
                                                {/* Sale Badge */}
                                                {product.is_on_sale && product.sale_discount_percentage > 0 && (
                                                    <div className="absolute top-3 right-3 z-10">
                                                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                                                            -{product.sale_discount_percentage}%
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Hover Overlay */}
                                                <div className="absolute inset-0 bg-brownSkin/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            </Link>
                                            
                                            {/* Quick Buy Button */}
                                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 translate-y-full group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                {product.available && product.is_in_stock ? (
                                                    <button 
                                                        onClick={() => addToCart(product.id, 1)}
                                                        className="bg-brownSkin text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-brownSkin/90 transition-colors duration-300 shadow-lg flex items-center gap-2 whitespace-nowrap"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01"/>
                                                        </svg>
                                                        {cartItems.some(item => item.id === product.id) ? translate('Added to cart') : translate("Add to cart")}
                                                    </button>
                                                ) : (
                                                    <button 
                                                        onClick={() => { setShowModal(true); setNotifyProductId(product.id); }}
                                                        className="bg-red-400 text-white px-4 py-2 rounded-full text-xs font-medium hover:bg-red-500 transition-colors duration-300 shadow-lg whitespace-nowrap"
                                                    >
                                                        {translate("Notify me when available")}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Product Info */}
                                        <div className="p-4">
                                            <Link to={`/proizvod/${product.category}/${product.path}`}>
                                                <div className="text-center">
                                                    <h3 className="font-semibold text-brownSkin mb-2 hover:text-brownSkin/80 transition-colors duration-300 line-clamp-2">
                                                        {localized(product, 'name', country)}
                                                    </h3>
                                                    <div className="flex justify-center">
                                                        <PriceDisplay product={product} currency={currency} className="text-lg font-bold text-brownSkin" />
                                                    </div>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Mystery Item - Coming Soon */}
                                <div className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden shadow-lg transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl border-2 border-dashed border-gray-300">
                                    {/* Mystery Image Container */}
                                    <div className="relative overflow-hidden aspect-square bg-gradient-to-br from-gray-200 to-gray-300">
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            {/* Question Mark Icon */}
                                            <div className="w-24 h-24 bg-gray-400 rounded-full flex items-center justify-center shadow-lg">
                                                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                </svg>
                                            </div>
                                        </div>
                                        {/* Coming Soon Overlay */}
                                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                                                <span className="text-sm font-bold text-gray-700">?</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mystery Product Info */}
                                    <div className="p-4">
                                        <div className="text-center">
                                            <h3 className="font-semibold text-gray-600 mb-2">
                                                {translate("Coming soon")}
                                            </h3>

                                            {/* Notify Button */}
                                            <button
                                                onClick={() => { setShowModal(true); setNotifyProductId(null); setNotifyCategoryId(slatkaCollection?.id || null); }}
                                                className="w-full bg-gray-400 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-500 transition-colors duration-300 shadow-lg"
                                            >
                                                {translate("Notify me when available")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* View All Partnership Products Button */}
                        <div className="text-center mt-12">
                            <Link 
                                to="/kolekcije/sve" 
                                className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full text-lg font-medium hover:bg-brownSkin hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                {translate("View all partnership products")}
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Radionice keramike Section */}
            <div className="bg-bgSkin py-20 mt-28">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        <div className="w-full md:w-1/2">
                            <div className="mb-8">
                                <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                                    {translate("Ceramics workshops")}
                                </h2>
                                <div className="w-24 h-1 bg-[#facb8c] rounded-full"></div>
                            </div>
                            <p className="text-gray-700 text-lg leading-relaxed mb-6">
                                {translate("Join our interactive ceramics workshops and discover the magic of creating with your own hands. You will learn the basic techniques of working with clay, shaping, and drawing. The workshops are suitable for all ages and experience levels — from beginners to those who want to perfect their skills.")}
                            </p>
                            <p className="text-gray-700 text-lg leading-relaxed mb-8">
                                {translate("Each participant will have the opportunity to create their own unique ceramic piece to take home as a keepsake after firing.")}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link 
                                    to="/radionice-keramike"
                                    className="bg-brownSkin text-white px-6 py-3 rounded-full text-lg font-medium hover:bg-[#8b5a32] transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    {translate("Reserve a spot")}
                                </Link>
                                <Link
                                    to="/radionice-keramike"
                                    className="bg-[#facb8c] text-black px-6 py-3 rounded-full text-lg font-medium hover:bg-[#f5b76b] transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    {translate("Learn more")}
                                </Link>
                            </div>
                        </div>
                        <div className="w-full md:w-1/2">
                            <div className="relative">
                                <img 
                                    className="w-full h-[500px] object-cover rounded-2xl shadow-lg" 
                                    src="/images/radionica/IMG_7633.webp" 
                                    alt="Radionica keramike - učesnici rade na keramičkim proizvodima" 
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                                {/* Workshop info overlay */}
                                <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-brownSkin font-medium">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                        <span>{translate("2-3 hours")}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-brownSkin font-medium mt-1">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                        </svg>
                                        <span>{translate("8-12 participants")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pakovanje Section */}
            <div className="w-full bg-white py-16 px-4 sm:px-8 lg:px-16">
                <div className="max-w-[1500px] mx-auto">
                    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

                        {/* Left — text */}
                        <div className="flex-1 space-y-8">
                            <div>
                                <span className="inline-block text-xs font-medium tracking-widest text-brownSkin/50 uppercase mb-3 border border-brownSkin/20 px-3 py-1 rounded-full">
                                    {translate("Packaging")}
                                </span>
                                <h2 className="text-3xl md:text-4xl font-bold text-brownSkin leading-tight">
                                    {translate("The box our little plates and mugs arrive in.")}
                                </h2>
                            </div>

                            <blockquote className="relative pl-5 border-l-2 border-brownSkin/30">
                                <p className="text-brownSkin/80 text-lg leading-relaxed italic">
                                    "{translate("This is the kind of box you treasure just as much as what's inside. 😍 Pure magic! ✨ I would never have the heart to throw it away. 🙈🥰")}"
                                </p>
                                <footer className="mt-3 text-sm text-brownSkin/40 not-italic">— {translate("customer comment")}</footer>
                            </blockquote>
                        </div>

                        {/* Right — box image */}
                        <div className="flex-1 flex justify-center lg:justify-end">
                            <div className="w-full sm:max-w-[420px] lg:max-w-[550px]">
                                <img
                                    src={require('../images/bulkaKutije.png')}
                                    alt="Bulka Ceramics pakovanje"
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced Story Section */}
            <div className="bg-slate-50 py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center gap-12 mb-20">
                        <div className="w-full md:w-1/2">
                            <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-6">{translate("Our Story")}</h2>
                            <p className="text-gray-700 text-lg leading-relaxed">
                                {translate("Bulka Ceramics is a family brand born from a love of creating unique ceramics. After years of perfecting our craft, we overcame challenges in the firing process and created collections that tell a story.")}
                            </p>
                        </div>
                        <div className="w-full md:w-1/2">
                            <img 
                                className="w-full h-[400px] object-cover rounded-2xl " 
                                src="/images/studio.jpg" 
                                alt="slika bullterriera u keramičom studiju" 
                            />
                        </div>
                    </div>

                    {/* Bulka Story Section */}
                    <div className="flex flex-col md:flex-row-reverse items-center gap-12 mb-20">
                        <div className="w-full md:w-1/2">
                            <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-6">{translate("Who is Bulka?")}</h2>
                            <p className="text-gray-700 text-lg leading-relaxed">
                                {translate("Bulka is the nickname of our beloved bull terrier Bea, who is the heart and soul of our brand. Her energy, persistence, and unique character inspire us in creating every ceramic piece.")}
                            </p>
                        </div>
                        <div className="w-full md:w-1/2">
                            <img 
                                className="w-full h-[400px] object-cover rounded-2xl " 
                                src="/images/bulka.jpg" 
                                alt="slika od bullterriera koji je simbol Bulke" 
                            />
                        </div>
                    </div>

                   
                </div>
            </div>

           

 
            <Footer categories={categories} />
        </div>
    )
}